import { useState } from "react";
import { Team } from "../graph";
import { usePersistentState } from "../usePersistentState";
import { fetchWithReporting } from "../errorReporter";

type Destination = "local" | "s3" | "sftp";

const DESTINATION_LABELS: Record<Destination, string> = {
  local: "Local download (zip)",
  s3: "AWS GovCloud S3",
  sftp: "SFTP server"
};

type Props = {
  graphToken: string | null;
  teamIds: string[];
  teams: Team[];
};

type ExportResult = {
  jobId: string;
  friendlyName?: string;
  destinations: Array<{
    destination: Destination;
    remotePath?: string;
    downloadUrl?: string;
    error?: string;
  }>;
  bytes: number;
  channels: number;
  messages: number;
  files?: number;
  fileBytes?: number;
};

export default function ExportPanel({ graphToken, teamIds, teams }: Props) {
  const [destinations, setDestinations] = usePersistentState<Destination[]>(
    "teamsagent.destinations",
    ["local"]
  );
  const [archiveAfter, setArchiveAfter] = usePersistentState<boolean>(
    "teamsagent.archiveAfter",
    false
  );
  const [emailOnComplete, setEmailOnComplete] = usePersistentState<boolean>(
    "teamsagent.emailOnComplete",
    true
  );
  const [lastFailedAction, setLastFailedAction] = useState<null | (() => void)>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [archiveResult, setArchiveResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState<Record<string, boolean>>({});

  const runConnectionTest = async (dest: "sftp" | "s3" | "snowflake") => {
    if (!graphToken) return;
    setTesting((p) => ({ ...p, [dest]: true }));
    setTestStatus((p) => ({ ...p, [dest]: "" }));
    try {
      const res = await fetch(`/api/test-${dest}`, {
        method: "POST",
        headers: { "x-graph-token": graphToken }
      });
      const data = await res.json();
      const warn =
        data.warnings?.length > 0 ? ` ⚠ ${data.warnings.join(" ⚠ ")}` : "";
      if (data.ok) {
        const msg =
          dest === "sftp"
            ? `✓ Connected to ${data.username}@${data.host}:${data.port} (${data.durationMs}ms). Listed ${data.remoteDir}: ${data.entries} entries.`
            : dest === "s3"
              ? `✓ HeadBucket + PutObject + DeleteObject succeeded against s3://${data.bucket}/${data.prefix} in ${data.region} (${data.durationMs}ms).`
              : `✓ Connected to ${data.account} as ${data.context?.user} (role ${data.context?.role}, warehouse ${data.context?.warehouse}, ${data.authMode} auth). ${data.context?.database}.${data.context?.schema}.AUDIT_EVENTS has ${data.auditRows} row(s) (${data.durationMs}ms).${warn}`;
        setTestStatus((p) => ({ ...p, [dest]: msg }));
      } else {
        const where = data.stage ? ` [stage: ${data.stage}]` : "";
        setTestStatus((p) => ({ ...p, [dest]: `✗${where} ${data.error}${warn}` }));
      }
    } catch (e) {
      setTestStatus((p) => ({
        ...p,
        [dest]: `✗ ${e instanceof Error ? e.message : String(e)}`
      }));
    } finally {
      setTesting((p) => ({ ...p, [dest]: false }));
    }
  };

  const toggleDest = (d: Destination) => {
    setDestinations((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    );
  };

  const runExport = async () => {
    if (!graphToken || teamIds.length === 0 || destinations.length === 0) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    setArchiveResult(null);
    try {
      const res = await fetchWithReporting(
        "/api/export",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-graph-token": graphToken
          },
          body: JSON.stringify({ teamIds, destinations, emailOnComplete })
        },
        "export"
      );
      const data: ExportResult = await res.json();
      setResult(data);

      if (archiveAfter) {
        const archRes = await fetchWithReporting(
          "/api/archive",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-graph-token": graphToken
            },
            body: JSON.stringify({ teamIds, jobId: data.jobId })
          },
          "archive"
        );
        const archData = await archRes.json();
        setArchiveResult(
          `Archived ${archData.archived} team(s). ${archData.failures?.length ? `Failures: ${archData.failures.length}` : ""}`
        );
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
      setLastFailedAction(() => runExport);
    } finally {
      setBusy(false);
    }
  };

  const selectedNames = teams
    .filter((t) => teamIds.includes(t.id))
    .map((t) => t.displayName);

  return (
    <div className="card">
      <h2>2. Configure &amp; export</h2>

      <div className="row" style={{ marginBottom: 16 }}>
        <div className="field">
          <label className="field-label">Destination(s)</label>
          {(Object.keys(DESTINATION_LABELS) as Destination[]).map((d) => (
            <div key={d}>
              <label style={{ display: "block" }}>
                <input
                  type="checkbox"
                  checked={destinations.includes(d)}
                  onChange={() => toggleDest(d)}
                />{" "}
                {DESTINATION_LABELS[d]}
                {(d === "sftp" || d === "s3") && (
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => runConnectionTest(d)}
                    disabled={testing[d]}
                    style={{ marginLeft: 8, padding: "2px 8px", fontSize: 12 }}
                  >
                    {testing[d] ? "Testing…" : "Test"}
                  </button>
                )}
              </label>
              {testStatus[d] && (
                <p
                  className={testStatus[d].startsWith("✓") ? "muted small" : "error"}
                  style={{ marginTop: 4, marginBottom: 8 }}
                >
                  {testStatus[d]}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="field">
          <label className="field-label">Audit logging (Snowflake)</label>
          <button
            type="button"
            className="secondary"
            onClick={() => runConnectionTest("snowflake")}
            disabled={testing["snowflake"]}
            style={{ padding: "2px 8px", fontSize: 12 }}
          >
            {testing["snowflake"] ? "Testing…" : "Test connection"}
          </button>
          {testStatus["snowflake"] && (
            <p
              className={testStatus["snowflake"].startsWith("✓") ? "muted small" : "error"}
              style={{ marginTop: 4, marginBottom: 8 }}
            >
              {testStatus["snowflake"]}
            </p>
          )}
        </div>

        <div className="field">
          <label className="field-label">Notifications</label>
          <label>
            <input
              type="checkbox"
              checked={emailOnComplete}
              onChange={(e) => setEmailOnComplete(e.target.checked)}
            />{" "}
            Email me each Team's PDFs as they complete
          </label>
        </div>

        <div className="field">
          <label className="field-label">After export</label>
          <label>
            <input
              type="checkbox"
              checked={archiveAfter}
              onChange={(e) => setArchiveAfter(e.target.checked)}
            />{" "}
            Archive Team(s) in Microsoft Teams
          </label>
        </div>
      </div>

      <p className="muted">
        {teamIds.length === 0
          ? "Select at least one Team above."
          : `Will export ${teamIds.length} Team(s) to ${destinations.length} destination(s): ${selectedNames.join(", ")}`}
      </p>

      <button
        onClick={() => {
          setLastFailedAction(null);
          runExport();
        }}
        disabled={busy || teamIds.length === 0 || destinations.length === 0}
      >
        {busy ? "Working…" : "Start export"}
      </button>

      {busy && <div className="loading-bar" style={{ marginTop: 12 }} />}

      {err && (
        <div className="error" style={{ marginTop: 16 }}>
          {err}{" "}
          {lastFailedAction && (
            <button
              type="button"
              className="secondary"
              onClick={() => {
                const fn = lastFailedAction;
                setLastFailedAction(null);
                fn();
              }}
              style={{ marginLeft: 8 }}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 16 }}>
          <p>
            <strong>Done.</strong> Job <code>{result.jobId}</code>{" "}
            <span className="pill">
              {result.channels} channels · {result.messages} messages
              {typeof result.files === "number" && ` · ${result.files} files`}
              {" "}· {(result.bytes / 1024 / 1024).toFixed(2)} MB zip
            </span>
          </p>
          <ul>
            {result.destinations.map((d) => (
              <li key={d.destination}>
                <strong>{DESTINATION_LABELS[d.destination]}:</strong>{" "}
                {d.error ? (
                  <span className="error" style={{ padding: "2px 8px" }}>{d.error}</span>
                ) : d.destination === "local" && d.downloadUrl ? (
                  <a
                    href={
                      result.friendlyName
                        ? `${d.downloadUrl}?name=${encodeURIComponent(result.friendlyName)}`
                        : d.downloadUrl
                    }
                    download={result.friendlyName ?? undefined}
                  >
                    Download {result.friendlyName ?? "zip"}
                  </a>
                ) : (
                  <code>{d.remotePath}</code>
                )}
              </li>
            ))}
          </ul>
          {archiveResult && <p className="muted">{archiveResult}</p>}
        </div>
      )}
    </div>
  );
}
